import { fabric } from "fabric";
import { CanvasHandler } from "../../../handlers";
import { ShapePropertiesEnum } from "../../../enums/ShapePropertiesEnum";
import { ObjectTypesEnum } from "../../../enums";

class ShapeProperties {
  public canvasHandler: CanvasHandler;
  public handlers: any

  constructor(canvasHandler: CanvasHandler) {
    this.canvasHandler = canvasHandler;
    this.handlers = {
      [ShapePropertiesEnum.Opacity]: this.updateOpacity,
      [ShapePropertiesEnum.Angle]: this.updateAngle,
      [ShapePropertiesEnum.Fill]: this.updateFill,
      [ShapePropertiesEnum.StrokeWidthShape]: this.updateStrokeWidthShape,
      [ShapePropertiesEnum.Stroke]: this.updateStroke,
      [ShapePropertiesEnum.VisibleText]: this.updateVisibleText,
      [ShapePropertiesEnum.TextText]: this.updateTextText,
      [ShapePropertiesEnum.LeftText]: this.updateLeftText,
      [ShapePropertiesEnum.TopText]: this.updateTopText,
      [ShapePropertiesEnum.FontSizeText]: this.updateFontSizeText,
      [ShapePropertiesEnum.FillText]: this.updateFillText,
      [ShapePropertiesEnum.AngleText]: this.updateAngleText,
      [ShapePropertiesEnum.VisibleIcon]: this.updateVisibleIcon,
      [ShapePropertiesEnum.TypeIconIcon]: this.updateTypeIconIcon,
      [ShapePropertiesEnum.LeftIcon]: this.updateLeftIcon,
      [ShapePropertiesEnum.TopIcon]: this.updateTopIcon,
      [ShapePropertiesEnum.Scaled]: this.updateScaled,
      [ShapePropertiesEnum.FillIcon]: this.updateFillIcon,
      [ShapePropertiesEnum.RotateIcon]: this.updateRotateIcon,
      [ShapePropertiesEnum.Rx]: this.updateRx,
      [ShapePropertiesEnum.Width]: this.updateWidth,
      [ShapePropertiesEnum.Ry]: this.updateRy,
      [ShapePropertiesEnum.Height]: this.updateHeight,
    }
  }

  public updateProperties = (
    selectedItem: any,
    key: string,
    value: any,
    allValues: any,
  ) => {
    const shape = selectedItem.item(0);
    const text = selectedItem.item(1);
    const icon = selectedItem.item(2);
    const old = {
      width: selectedItem.width,
      height: selectedItem.height,
      left: selectedItem.left,
      top: selectedItem.top,
    };
    const handler = this.handlers[key];

    if (!handler) return;
    
    handler({
      selectedItem,
      shape,
      text,
      icon,
      old,
      value,
      key,
    });
    this.refreshGroup(selectedItem);
  };

  private updateOpacity = ({selectedItem, value}: any) => {
    selectedItem.set('opacity', value);
  }

  private updateAngle = ({selectedItem, value}: any) => {
    selectedItem.rotate(value);
  }

  private updateFill = ({selectedItem, shape, value}: any) => {
    shape.set({
      fill: value,
    });
  }

  private updateStrokeWidthShape = ({selectedItem, shape, value, old}: any) => {
    if(typeof value == 'string' || value == null || isNaN(value) || value < 1){
      value = 1;
    }
    if(value > 30){
      value = 30
    }
    shape.set("strokeWidth", value);
    selectedItem.addWithUpdate();
    let widthDiff = Math.abs(selectedItem.width-old.width);
    let heightDiff = Math.abs(selectedItem.height-old.height);
    selectedItem.set({
      left: selectedItem.width>old.width ? old.left-widthDiff/2 : old.left+widthDiff/2,
      top: selectedItem.height>old.height ? old.top-heightDiff/2 : old.top+heightDiff/2,
    });

  }

  private updateStroke = ({selectedItem, shape, value}: any) => {
    shape.set("stroke", value);
  }

  private updateVisibleText = ({selectedItem, text, value}: any) => {
    text.set({
      visible: value
    });
    selectedItem.set({
      visibleText: value
    });
  }

  private updateTextText = ({selectedItem, text, value}: any) => {
    text.set({
      text: value,
      left: 0
    });
    selectedItem.addWithUpdate();
    selectedItem.setCoords();
    text.set({
      left: text.left-(text.width / 2)
    })
  }

  private updateLeftText = ({text, value}: any) => {
    text.set('left', value)
  }

  private updateTopText = ({text, value}: any) => {
    text.set('top', value)
  }

  private updateFontSizeText = ({text, value}: any) => {
    text.set('fontSize', value)
  }

  private updateFillText = ({text, value}: any) => {
    text.set({
      fill: value,
    });
  }

  private updateAngleText = ({text, value}: any) => {
    text.set('angle', value)
  }

  private updateVisibleIcon = ({selectedItem, icon, value}: any) => {
    icon.set({
      visible: value
    });
    selectedItem.set({
      visibleIcon: value
    });
  }

  private updateTypeIconIcon = ({selectedItem, icon, value}: any) => {
    let svgConverted, serializer, svgStr;
    let svgEl = value;

    svgConverted = document.createRange().createContextualFragment(svgEl);
    serializer = new XMLSerializer();
    svgStr = serializer.serializeToString(svgConverted);

    let containerId = this.canvasHandler.container.id;
    let loadedObject: any;

    fabric.loadSVGFromString(svgStr, function(objects, options) {
      loadedObject = fabric.util.groupSVGElements(objects, options);
      loadedObject.set({
        id: icon.id,
        hasControls: icon.editable,
        hasBorders: icon.editable,
        selectable: icon.editable,
        lockMovementX: icon.editable,
        lockMovementY: icon.editable,
        hoverCursor: !icon.editable ? "pointer" : "move",
        container: containerId,
        editable: icon.editable,
        fill: icon.fill,
        left: selectedItem.left+(selectedItem.width/2)+icon.left,
        top: selectedItem.top+(selectedItem.height/2)+icon.top,
        originX: "left",
        originY: "top",
        opacity: 1,
        objectCaching: !icon.editable,
        superType: ObjectTypesEnum.Svg,
        type: ObjectTypesEnum.Svg,
        name: "New icon SVG",
        scaleX: icon.scaleX,
        scaleY: icon.scaleY,
        svg: svgEl,
        angle: icon.angle,
        loadType: ObjectTypesEnum.Svg,
        scaled: icon.scaled,
        originHeight: loadedObject.height,
        originWidth: loadedObject.width,
        rotated: 0
      });
    });
    icon._objects.forEach((path: any) => {
      path.set("fill", value);
      path.setCoords();
    });
    selectedItem.addWithUpdate(loadedObject);
    selectedItem.removeWithUpdate(selectedItem._objects[2]);
    selectedItem.setCoords();
    loadedObject.scaleToWidth((loadedObject.originWidth*loadedObject.scaled));
    loadedObject.scaleToHeight((loadedObject.originHeight*loadedObject.scaled));
    loadedObject.setCoords();
  }

  private updateLeftIcon = ({icon, value}: any) => {
    icon.set('left', value)
  }

  private updateTopIcon = ({icon, value}: any) => {
    icon.set('top', value)
  }

  private updateScaled = ({icon, value}: any) => {
    icon.set({
      scaled: value,
    })
    icon.scaleToWidth((icon.originWidth*value));
    icon.scaleToHeight((icon.originHeight*value));
    icon.setCoords();
  }

  private updateFillIcon = ({icon, value}: any) => {
    icon.set({
      fill: value,
    });
    icon._objects.forEach((path: any) => {
      path.set("fill", value);
      path.setCoords();
    });
  }

  private updateRotateIcon = ({icon, value}: any) => {
    icon.rotate(value);
    icon.set({
      rotated: value
    })
  }

  private updateRx = ({shape, value}: any) => {
    shape.set('rx', value);
    shape.setCoords();
  }

  private updateWidth = ({shape, value}: any) => {
    shape.set('width', value);
    shape.setCoords();
  }

  private updateRy = ({shape, value}: any) => {
    shape.set('ry', value);
    shape.setCoords();
  }

  private updateHeight = ({shape, value}: any) => {
    shape.set('height', value);
    shape.setCoords();
  }

  private refreshGroup(selectedItem: any) {
    selectedItem.addWithUpdate();
    selectedItem.setCoords();
    this.canvasHandler.canvas.renderAll();
  }
}

export default ShapeProperties;